// src/components/MeetingPage.jsx
// Meeting Module — real-time transcription via Deepgram WebSocket
// Entry points:
//   1. Nav bar / dashboard button   → shows account picker modal first
//   2. Account Detail "New Meeting" → presetAccountId passed as prop (skips picker)
//   3. URL param ?accountId=xxx     → same as #2 for direct links

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './MeetingPage.css';
import { apiFetch } from '../lib/api';

// ── States ──────────────────────────────────────────────────────────────────
// idle → ready → recording → stopped → processing → done

export default function MeetingPage({ onNavigate, presetAccountId = null, presetAccountName = null }) {

  // ── Parse URL params for account context (e.g. from AccountDetailPage link) ──
  const urlParams      = new URLSearchParams(window.location.search);
  const urlAccountId   = urlParams.get('accountId');
  const urlAccountName = urlParams.get('accountName');

  const initAccountId   = presetAccountId   || urlAccountId   || null;
  const initAccountName = presetAccountName || urlAccountName || null;

  // Recording state
  const [phase, setPhase]             = useState('idle');
  const [transcript, setTranscript]   = useState('');
  const [interimText, setInterimText] = useState('');
  const [duration, setDuration]       = useState(0);
  const [error, setError]             = useState(null);

  // Account assignment
  const [accounts, setAccounts]             = useState([]);
  const [accountSearch, setAccountSearch]   = useState('');
  const [showDropdown, setShowDropdown]     = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(
    initAccountId ? { id: initAccountId, name: initAccountName || 'Account' } : null
  );

  // Meeting context (Account Meeting | Tradeshow / Event | Ad Hoc)
  const [meetingContext, setMeetingContext] = useState(
    initAccountId ? 'Account Meeting' : null
  );
  const [eventName, setEventName]           = useState('');
  const [meetingTitle, setMeetingTitle]     = useState('');
  const [meetingType, setMeetingType]       = useState(null);   // e.g. 'RFP Review'
  const [opportunityId, setOpportunityId]   = useState(null);
  const [opportunityName, setOpportunityName] = useState('');
  const [opportunities, setOpportunities]   = useState([]);
  const [oppSearch, setOppSearch]           = useState('');
  const [showOppDropdown, setShowOppDropdown] = useState(false);
  const oppSearchRef                        = useRef(null);

  // Account picker modal — shown when no preset account and phase is idle
  const [showPicker, setShowPicker]         = useState(false);
  const [pickerMode, setPickerMode]         = useState(null); // 'account' | 'event' | 'adhoc'

  // Processing / results
  const [results, setResults]               = useState(null);
  const [processingStep, setProcessingStep] = useState('');
  const [showLinkBanner, setShowLinkBanner] = useState(false);

  // Editable contacts (populated from Jarvis results, user can correct before Victor re-runs)
  const [editableContacts, setEditableContacts] = useState([]);
  const [contactsDirty, setContactsDirty]       = useState(false);
  const [rerunStatus, setRerunStatus]           = useState(null); // null | 'running' | 'done' | 'error'

  // Link banner state (lifted out of renderLinkBanner to satisfy hooks rules)
  const [bannerSearch, setBannerSearch]     = useState('');
  const [bannerOpen, setBannerOpen]         = useState(false);
  // A rejected link has to be SAYABLE. Without somewhere to put the reason, the
  // only honest options were to hide the banner (a lie) or leave it up with no
  // explanation (a mystery).
  const [linkError, setLinkError]           = useState('');
  const bannerRef                           = useRef(null);

  // Refs
  const socketRef     = useRef(null);
  const mediaRef      = useRef(null);
  const timerRef      = useRef(null);
  const transcriptRef = useRef(null);
  const searchRef     = useRef(null);
  const resultsRef    = useRef(null);

  const isPreset = !!(presetAccountId || urlAccountId);

  // ── Load accounts + opportunities ─────────────────────────────────────────
  useEffect(() => {
    apiFetch('/api/accounts/list')
      .then(r => r.json())
      .then(d => setAccounts(d.accounts || []))
      .catch(() => {});
    apiFetch('/api/opportunities/list')
      .then(r => r.json())
      .then(d => setOpportunities(d.opportunities || []))
      .catch(() => {});
  }, []);

  // ── Show picker on mount if no account context ─────────────────────────────
  useEffect(() => {
    if (!initAccountId) {
      setShowPicker(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Duration timer ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase === 'recording') {
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // ── Auto-scroll transcript ─────────────────────────────────────────────────
  useEffect(() => {
    if (phase === 'done' && resultsRef.current) {
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [phase]);
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcript, interimText]);

  // ── Close dropdown on outside click ───────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const formatDuration = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  // ── Filtered accounts for search ───────────────────────────────────────────
  const filteredAccounts = accounts.filter(a =>
    !accountSearch || a.accountName?.toLowerCase().includes(accountSearch.toLowerCase())
  ).slice(0, 8);

  // ── Confirm picker selection and close modal ───────────────────────────────
  const confirmPicker = () => {
    if (pickerMode === 'account' && !selectedAccount) return; // must pick one
    if (pickerMode === 'event' && !eventName.trim()) return;  // must enter event name
    if (!pickerMode) return;
    setShowPicker(false);
  };

  // ── Get Deepgram token ─────────────────────────────────────────────────────
  const getDeepgramToken = async () => {
    const res = await apiFetch('/api/meetings/token');
    if (!res.ok) throw new Error('Could not get transcription token');
    const data = await res.json();
    return data.token;
  };

  // ── Start recording ────────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    setError(null);
    setPhase('ready');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      mediaRef.current = stream;
      const token = await getDeepgramToken();

      const params = new URLSearchParams({
        model:            'nova-2',
        language:         'en-US',
        smart_format:     'true',
        punctuate:        'true',
        diarize:          'true',
        interim_results:  'true',
        utterance_end_ms: '1000',
      });

      const socket = new WebSocket(`wss://api.deepgram.com/v1/listen?${params}`, ['token', token]);
      socketRef.current = socket;

      socket.onopen = () => {
        setPhase('recording');
        setDuration(0);
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRef.current.recorder = mediaRecorder;
        mediaRecorder.ondataavailable = (e) => {
          if (socket.readyState === WebSocket.OPEN && e.data.size > 0) socket.send(e.data);
        };
        mediaRecorder.start(250);
      };

      socket.onmessage = (e) => {
        const data = JSON.parse(e.data);
        if (data.type !== 'Results') return;
        const alt  = data.channel?.alternatives?.[0];
        if (!alt) return;
        const text = alt.transcript;
        if (!text) return;
        if (data.is_final) {
          setTranscript(prev => prev + (prev ? ' ' : '') + text);
          setInterimText('');
        } else {
          setInterimText(text);
        }
      };

      socket.onerror = () => {
        setError('Transcription connection error. Check your Deepgram API key.');
        if (mediaRef.current?.recorder)   mediaRef.current.recorder.stop();
        if (mediaRef.current?.getTracks)  mediaRef.current.getTracks().forEach(t => t.stop());
        if (socketRef.current)            socketRef.current.close();
        setPhase('idle');
      };

      socket.onclose = () => {
        setPhase(prev => prev === 'recording' ? 'stopped' : prev);
      };

    } catch (err) {
      setError(err.message || 'Could not start recording');
      setPhase('idle');
      if (mediaRef.current) mediaRef.current.getTracks?.().forEach(t => t.stop());
    }
  }, []);

  // ── Stop recording ─────────────────────────────────────────────────────────
  const stopRecording = useCallback(() => {
    if (mediaRef.current?.recorder)  mediaRef.current.recorder.stop();
    if (mediaRef.current?.getTracks) mediaRef.current.getTracks().forEach(t => t.stop());
    if (socketRef.current)           socketRef.current.close();
    setTranscript(prev => prev + (interimText ? (prev ? ' ' : '') + interimText : ''));
    setInterimText('');
    setPhase('stopped');
  }, [interimText]);

  // ── Process transcript — two-phase to beat Vercel timeout ──────────────────
  // Phase A: Call /api/meetings/process (Jarvis only — fast, ~5s) → returns immediately
  //          with Jarvis analysis + kicks off async Victor+Cyrano pipeline server-side
  // Phase B: Poll /api/meetings/status?id=xxx every 3s until victor_complete
  const processTranscript = useCallback(async () => {
    if (!transcript.trim()) { setError('No transcript to process'); return; }
    setPhase('processing');
    setError(null);
    setProcessingStep('Jarvis is analyzing your meeting…');

    // Update processing step after a few seconds so Gary knows it's working
    const stepTimer1 = setTimeout(() => setProcessingStep('Extracting tasks, signals and insights…'), 3000);
    const stepTimer2 = setTimeout(() => setProcessingStep('Writing to CRM and kicking off Victor review…'), 7000);

    try {
      const res = await apiFetch('/api/meetings/process', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcript,
          accountId:      selectedAccount?.id   || null,
          accountName:    selectedAccount?.name || 'Unknown',
          meetingTitle:   meetingTitle || 'Sales Meeting',
          meetingDate:    new Date().toISOString().slice(0, 10),
          meetingContext:  meetingContext || 'Ad Hoc',
          meetingType:     meetingType || null,
          eventName:       eventName || null,
          opportunityId:   opportunityId || null,
          opportunityName: opportunityName || null,
          durationMinutes: Math.round(duration / 60),
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Server error ${res.status}`);
      }

      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Processing failed');

      setResults(data);
      setPhase('done');
      setProcessingStep('');
      clearTimeout(stepTimer1);
      clearTimeout(stepTimer2);
      // Seed editable contacts from Jarvis output
      setEditableContacts((data.analysis?.contactsDetected || []).map(c => ({ ...c })));
      setContactsDirty(false);
      setRerunStatus(null);

      // Show link banner if meeting was saved without an account
      if (!selectedAccount && !data.accountId) {
        setShowLinkBanner(true);
      }

    } catch (err) {
      setError(err.message || 'Processing failed. Please try again.');
      setPhase('stopped');
      setProcessingStep('');
      clearTimeout(stepTimer1);
      clearTimeout(stepTimer2);
    }
  }, [transcript, selectedAccount, meetingTitle, meetingContext, meetingType, eventName, opportunityId, opportunityName, duration]);

  // ── Link unlinked meeting to an account retroactively ─────────────────────
  const linkToAccount = async (account) => {
    if (!results?.results?.meetingRecord?.id) return;
    try {
      // POST and `id`, not PATCH and `meetingId`. Both were wrong and both failed
      // silently: the handler is withApi(..., { methods: ['POST'] }) so PATCH
      // answered 405, and it destructures `id` from the body so `meetingId` would
      // have been undefined even once the verb was right.
      const res = await apiFetch('/api/meetings/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: results.results.meetingRecord.id,
          accountId: account.id,
          meetingContext: 'Account Meeting',
        }),
      });
      const data = await res.json().catch(() => ({}));
      // res.ok, not the absence of a throw. apiFetch RESOLVES on 4xx/5xx, so the
      // catch below only ever saw a TRANSPORT failure — which is why the banner hid
      // itself and the account appeared selected after a request the server had
      // rejected with a 405. The state below claims the write landed, so it may only
      // run when the server says it did.
      if (!res.ok || data.success === false) {
        setLinkError(data.error || ('Could not link this meeting (HTTP ' + res.status + ')'));
        return;
      }
      setLinkError('');
      setSelectedAccount(account);
      setShowLinkBanner(false);
    } catch (err) {
      console.error('Failed to link meeting to account:', err);
      setLinkError('Could not reach the server to link this meeting.');
    }
  };

  // ── Update contacts + re-run Victor with corrected attendees ────────────────
  const rerunVictor = async () => {
    if (!results?.results?.meetingRecord?.id) return;
    setRerunStatus('running');

    // 1. Patch Notion meeting record with corrected contact names
    const contactNames = editableContacts.map(c => c.name).join(', ');
    try {
      // See the note at the sibling call above: POST, and `id` in the body.
      //
      // contactsOverride writes meetings.contacts_mentioned, which process.js:188
      // already fills with `contactsDetected.map(c => c.name).join(', ')` — the same
      // shape from the same source — and MeetingsTab displays read-only. A human
      // correcting that list is the better version of the same fact.
      const res = await apiFetch('/api/meetings/update', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id:               results.results.meetingRecord.id,
          contactsOverride: contactNames,
        }),
      });
      // Warn rather than abort: the re-run below passes the corrected attendees to
      // process-victor directly, so it still produces the right analysis even if
      // recording the correction failed.
      if (!res.ok) console.warn('[MeetingPage] contact update failed:', res.status);
    } catch (err) {
      console.warn('Contact update failed:', err);
    }

    // 2. Re-fire process-victor with corrected contacts baked into jarvisAnalysis
    const correctedJarvis = {
      ...results.analysis,
      contactsDetected:  editableContacts,
      externalAttendees: editableContacts.filter(c => !c.isInternal).map(c => c.name),
      internalAttendees: editableContacts.filter(c =>  c.isInternal).map(c => c.name),
    };

    try {
      await apiFetch('/api/meetings/process-victor', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meetingPageId:  results.results.meetingRecord.id,
          transcript,
          accountId:      results.accountId || selectedAccount?.id || null,
          accountName:    selectedAccount?.name || 'Unknown',
          meetingTitle:   meetingTitle || 'Sales Meeting',
          meetingDate:    new Date().toISOString().slice(0, 10),
          meetingContext: results.meetingContext || meetingContext,
          eventName:      results.eventName || eventName || null,
          jarvisAnalysis: correctedJarvis,
        }),
      });
      setRerunStatus('done');
      setContactsDirty(false);
    } catch (err) {
      console.error('Re-run Victor failed:', err);
      setRerunStatus('error');
    }
  };

  // ── Restart / discard ──────────────────────────────────────────────────────
  const restart = () => {
    setPhase('idle');
    setTranscript('');
    setInterimText('');
    setDuration(0);
    setResults(null);
    setError(null);
    setProcessingStep('');
    setShowLinkBanner(false);
    setEditableContacts([]);
    setContactsDirty(false);
    setRerunStatus(null);
    if (!isPreset) {
      setMeetingType(null);
      setOpportunityId(null);
      setOpportunityName('');
      setSelectedAccount(null);
      setMeetingContext(null);
      setEventName('');
      setPickerMode(null);
      setShowPicker(true);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER — ACCOUNT PICKER MODAL
  // ─────────────────────────────────────────────────────────────────────────
  const renderPickerModal = () => (
    <div className="mtg-picker-overlay">
      <div className="mtg-picker-modal">
        <div className="mtg-picker-header">
          <div className="mtg-picker-icon">🎙️</div>
          <div className="mtg-picker-title">Which account is this meeting for?</div>
          <div className="mtg-picker-sub">You can also link it to an account after the meeting ends.</div>
        </div>

        {/* Option cards */}
        {!pickerMode && (
          <div className="mtg-picker-options">
            <button
              className="mtg-picker-option"
              onClick={() => { setPickerMode('account'); setMeetingContext('Account Meeting'); }}
            >
              <span className="mtg-picker-option-icon">🏢</span>
              <div>
                <div className="mtg-picker-option-label">Account Meeting</div>
                <div className="mtg-picker-option-desc">Link to an existing account in DigitalMe</div>
              </div>
            </button>

            <button
              className="mtg-picker-option"
              onClick={() => { setPickerMode('event'); setMeetingContext('Tradeshow / Event'); }}
            >
              <span className="mtg-picker-option-icon">🎪</span>
              <div>
                <div className="mtg-picker-option-label">Tradeshow / Event</div>
                <div className="mtg-picker-option-desc">Conference, industry event, or trade show</div>
              </div>
            </button>

            <button
              className="mtg-picker-option"
              onClick={() => {
                setPickerMode('adhoc');
                setMeetingContext('Ad Hoc');
                setSelectedAccount(null);
                setShowPicker(false);
              }}
            >
              <span className="mtg-picker-option-icon">⚡</span>
              <div>
                <div className="mtg-picker-option-label">Ad Hoc / No Account</div>
                <div className="mtg-picker-option-desc">Unplanned conversation — link later if needed</div>
              </div>
            </button>
          </div>
        )}

        {/* Account search sub-panel */}
        {pickerMode === 'account' && (
          <div className="mtg-picker-subpanel">
            <button className="mtg-picker-back" onClick={() => { setPickerMode(null); setSelectedAccount(null); }}>
              ← Back
            </button>
            <div className="mtg-field" ref={searchRef}>
              <label className="mtg-label">Search Accounts</label>
              {selectedAccount ? (
                <div className="mtg-account-selected">
                  <span className="mtg-account-icon">🏢</span>
                  <span className="mtg-account-name">{selectedAccount.name}</span>
                  <button className="mtg-account-clear" onClick={() => setSelectedAccount(null)}>✕</button>
                </div>
              ) : (
                <>
                  <input
                    className="mtg-input"
                    placeholder="Type to search accounts…"
                    value={accountSearch}
                    autoFocus
                    onChange={e => { setAccountSearch(e.target.value); setShowDropdown(true); }}
                    onFocus={() => setShowDropdown(true)}
                  />
                  {showDropdown && filteredAccounts.length > 0 && (
                    <div className="mtg-account-dropdown">
                      {filteredAccounts.map(a => (
                        <div
                          key={a.id}
                          className="mtg-account-option"
                          onClick={() => {
                            setSelectedAccount({ id: a.id, name: a.accountName });
                            setAccountSearch('');
                            setShowDropdown(false);
                          }}
                        >
                          <span>🏢</span>
                          <span>{a.accountName}</span>
                          {a.tier && <span className="mtg-account-tier">{a.tier}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            {/* Meeting Type selector — shown once account is picked */}
            {selectedAccount && (
              <>
                <div className="mtg-field" style={{ marginTop: 12 }}>
                  <label className="mtg-label">Meeting Type</label>
                  <select
                    className="mtg-input mtg-select"
                    value={meetingType || ''}
                    onChange={e => setMeetingType(e.target.value || null)}
                  >
                    <option value="">Select type…</option>
                    <option value="Client Discovery">Client Discovery</option>
                    <option value="Relationship / Check-in">Relationship / Check-in</option>
                    <option value="Technical Deep Dive">Technical Deep Dive</option>
                    <option value="QBR / Executive Review">QBR / Executive Review</option>
                    <option value="Internal Pursuit Review">Internal Pursuit Review</option>
                    <option value="RFP Review">RFP Review</option>
                    <option value="Proposal Review">Proposal Review</option>
                    <option value="Debrief">Debrief</option>
                  </select>
                </div>

                {/* Opportunity link — shown for internal/pursuit meeting types */}
                {['Internal Pursuit Review', 'RFP Review', 'Proposal Review', 'Debrief'].includes(meetingType) && (
                  <div className="mtg-field" ref={oppSearchRef}>
                    <label className="mtg-label">
                      Link to Opportunity <span className="mtg-label-optional">(recommended)</span>
                    </label>
                    {opportunityId ? (
                      <div className="mtg-account-selected">
                        <span className="mtg-account-icon">🎯</span>
                        <span className="mtg-account-name">{opportunityName}</span>
                        <button className="mtg-account-clear" onClick={() => { setOpportunityId(null); setOpportunityName(''); }}>✕</button>
                      </div>
                    ) : (
                      <>
                        <input
                          className="mtg-input"
                          placeholder="Search opportunities…"
                          value={oppSearch}
                          onChange={e => { setOppSearch(e.target.value); setShowOppDropdown(true); }}
                          onFocus={() => setShowOppDropdown(true)}
                        />
                        {showOppDropdown && (
                          <div className="mtg-account-dropdown">
                            {opportunities
                              .filter(o => !oppSearch || o.name?.toLowerCase().includes(oppSearch.toLowerCase()))
                              .slice(0, 8)
                              .map(o => (
                                <div
                                  key={o.id}
                                  className="mtg-account-option"
                                  onClick={() => {
                                    setOpportunityId(o.id);
                                    setOpportunityName(o.name);
                                    setOppSearch('');
                                    setShowOppDropdown(false);
                                  }}
                                >
                                  <span>🎯</span>
                                  <span>{o.name}</span>
                                </div>
                              ))}
                            {opportunities.filter(o => !oppSearch || o.name?.toLowerCase().includes(oppSearch.toLowerCase())).length === 0 && (
                              <div className="mtg-account-option" style={{ opacity: 0.5, cursor: 'default' }}>No opportunities found</div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    <div className="mtg-field-hint">Victor will use the opportunity context to guide pursuit strategy.</div>
                  </div>
                )}

                {/* Internal meeting notice */}
                {['Internal Pursuit Review', 'RFP Review', 'Proposal Review', 'Debrief'].includes(meetingType) && (
                  <div className="mtg-internal-notice">
                    🔒 Internal meeting — Victor will generate pursuit strategy instead of client outreach. No thank-you email will be drafted.
                  </div>
                )}
              </>
            )}

            <button
              className="mtg-picker-confirm"
              onClick={confirmPicker}
              disabled={!selectedAccount}
            >
              Continue →
            </button>
          </div>
        )}

        {/* Event name sub-panel */}
        {pickerMode === 'event' && (
          <div className="mtg-picker-subpanel">
            <button className="mtg-picker-back" onClick={() => { setPickerMode(null); setEventName(''); }}>
              ← Back
            </button>
            <div className="mtg-field">
              <label className="mtg-label">Event Name</label>
              <input
                className="mtg-input"
                placeholder="e.g. Industry Summit 2026 Annual Conference"
                value={eventName}
                autoFocus
                onChange={e => setEventName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && eventName.trim() && confirmPicker()}
              />
            </div>
            <button
              className="mtg-picker-confirm"
              onClick={confirmPicker}
              disabled={!eventName.trim()}
            >
              Continue →
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER — LINK BANNER (post-meeting, unlinked)
  // ─────────────────────────────────────────────────────────────────────────
  const renderLinkBanner = () => {
    const filtered = accounts.filter(a =>
      !bannerSearch || a.accountName?.toLowerCase().includes(bannerSearch.toLowerCase())
    ).slice(0, 6);

    return (
      <div className="mtg-link-banner">
        <span className="mtg-link-banner-icon">🔗</span>
        <span className="mtg-link-banner-text">This meeting isn't linked to an account.</span>
        <div className="mtg-link-banner-search" ref={bannerRef}>
          <input
            className="mtg-link-input"
            placeholder="Search accounts to link…"
            value={bannerSearch}
            onChange={e => { setBannerSearch(e.target.value); setBannerOpen(true); }}
            onFocus={() => setBannerOpen(true)}
          />
          {bannerOpen && filtered.length > 0 && (
            <div className="mtg-link-dropdown">
              {filtered.map(a => (
                <div
                  key={a.id}
                  className="mtg-link-option"
                  onClick={() => linkToAccount({ id: a.id, name: a.accountName })}
                >
                  🏢 {a.accountName}
                </div>
              ))}
            </div>
          )}
        </div>
        <button className="mtg-link-dismiss" onClick={() => setShowLinkBanner(false)}>Dismiss</button>
        {linkError && (
          <span className="mtg-link-error" role="alert">{linkError}</span>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER — ACCOUNT CONTEXT BADGE (shown during/after recording)
  // ─────────────────────────────────────────────────────────────────────────
  const renderContextBadge = () => {
    if (selectedAccount) {
      return (
        <div className="mtg-context-badge mtg-context-account">
          🏢 {selectedAccount.name}
        </div>
      );
    }
    if (meetingContext === 'Tradeshow / Event' && eventName) {
      return (
        <div className="mtg-context-badge mtg-context-event">
          🎪 {eventName}
        </div>
      );
    }
    if (meetingContext === 'Ad Hoc') {
      return (
        <div className="mtg-context-badge mtg-context-adhoc">
          ⚡ Ad Hoc
        </div>
      );
    }
    return null;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="mtg-root">

      {/* Picker modal — shown until user selects context */}
      {showPicker && renderPickerModal()}

      {/* Header */}
      <div className="mtg-header">
        <div className="mtg-header-left">
          <div className="mtg-avatar">🎙️</div>
          <div>
            <div className="mtg-page-title">Meeting Studio</div>
            <div className="mtg-page-sub">Real-time transcription · AI analysis · Auto-sync to CRM</div>
          </div>
        </div>
        <div className="mtg-header-right">
          {renderContextBadge()}
          {phase === 'recording' && (
            <div className="mtg-live-badge">
              <div className="mtg-live-dot" />
              LIVE · {formatDuration(duration)}
            </div>
          )}
          {phase === 'stopped' && (
            <div className="mtg-duration-badge">{formatDuration(duration)} recorded</div>
          )}
          {phase === 'done' && (
            <div className="mtg-done-badge">✓ Synced to CRM</div>
          )}
        </div>
      </div>

      {/* Link banner for unlinked meetings */}
      {showLinkBanner && renderLinkBanner()}

      {/* Body */}
      <div className="mtg-body">

        {/* ── Left: Setup + Transcript ── */}
        <div className="mtg-left">

          {/* Setup card */}
          {(phase === 'idle' || phase === 'ready') && !showPicker && (
            <div className="mtg-setup-card dm-fade-in">
              <div className="mtg-setup-title">New Meeting</div>

              {/* Context summary (read-only, set by picker) */}
              <div className="mtg-context-summary">
                {selectedAccount && (
                  <div className="mtg-context-row">
                    <span className="mtg-context-label">Account:</span>
                    <span className="mtg-context-value">🏢 {selectedAccount.name}</span>
                    {!isPreset && (
                      <button className="mtg-context-change" onClick={() => { setSelectedAccount(null); setPickerMode('account'); setShowPicker(true); }}>Change</button>
                    )}
                  </div>
                )}
                {meetingContext === 'Tradeshow / Event' && (
                  <div className="mtg-context-row">
                    <span className="mtg-context-label">Event:</span>
                    <span className="mtg-context-value">🎪 {eventName}</span>
                    <button className="mtg-context-change" onClick={() => { setPickerMode('event'); setShowPicker(true); }}>Change</button>
                  </div>
                )}
                {meetingContext === 'Ad Hoc' && (
                  <div className="mtg-context-row">
                    <span className="mtg-context-label">Type:</span>
                    <span className="mtg-context-value">⚡ Ad Hoc — No Account</span>
                    <button className="mtg-context-change" onClick={() => { setPickerMode(null); setShowPicker(true); }}>Change</button>
                  </div>
                )}
              </div>

              {/* Meeting title */}
              <div className="mtg-field">
                <label className="mtg-label">Meeting Title</label>
                <input
                  className="mtg-input"
                  placeholder="e.g. Discovery call with Ohio Mutual"
                  value={meetingTitle}
                  onChange={e => setMeetingTitle(e.target.value)}
                  disabled={phase === 'ready'}
                />
              </div>

              {error && <div className="mtg-error">{error}</div>}

              <button
                className="mtg-start-btn"
                onClick={startRecording}
                disabled={phase === 'ready'}
              >
                {phase === 'ready' ? (
                  <><span className="mtg-btn-spinner" /> Connecting…</>
                ) : (
                  <><span className="mtg-btn-icon">🎙️</span> Start Recording</>
                )}
              </button>

              <div className="mtg-mic-note">
                Microphone access required · Works for in-person and virtual calls
              </div>
            </div>
          )}

          {/* Transcript card */}
          {(phase === 'recording' || phase === 'stopped' || phase === 'processing' || phase === 'done') && (
            <div className="mtg-transcript-card dm-fade-in">
              <div className="mtg-transcript-header">
                <div className="mtg-transcript-label">
                  {phase === 'recording' ? (
                    <><div className="mtg-rec-dot" /> Transcribing live…</>
                  ) : 'Transcript'}
                </div>
                <div className="mtg-transcript-wordcount">
                  {transcript.split(/\s+/).filter(Boolean).length} words
                </div>
              </div>

              <div className="mtg-transcript-body" ref={transcriptRef}>
                {transcript ? (
                  <p className="mtg-transcript-text">
                    {transcript}
                    {interimText && <span className="mtg-interim"> {interimText}</span>}
                  </p>
                ) : (
                  <div className="mtg-transcript-empty">
                    {phase === 'recording'
                      ? 'Start speaking — your words will appear here in real time.'
                      : 'No transcript captured.'}
                  </div>
                )}
              </div>

              {/* Controls */}
              {phase === 'recording' && (
                <div className="mtg-controls">
                  <button className="mtg-stop-btn" onClick={stopRecording}>⏹ Stop Recording</button>
                </div>
              )}

              {phase === 'stopped' && (
                <div className="mtg-controls">
                  <button className="mtg-discard-btn" onClick={restart}>Discard</button>
                  <button
                    className="mtg-process-btn"
                    onClick={processTranscript}
                    disabled={!transcript.trim()}
                  >
                    ⚡ Analyze &amp; Sync to CRM
                  </button>
                </div>
              )}

              {phase === 'processing' && (
                <div className="mtg-controls">
                  <div className="mtg-processing-indicator">
                    <div className="mtg-processing-spinner" />
                    {processingStep || 'Processing…'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Right: Results ── */}
        <div className="mtg-right">

          {/* Waiting / idle state */}
          {(phase === 'idle' || phase === 'ready' || phase === 'recording') && (
            <div className="mtg-results-placeholder">
              <div className="mtg-placeholder-icon">🧠</div>
              <div className="mtg-placeholder-title">AI Analysis</div>
              <div className="mtg-placeholder-sub">
                After you stop recording, Jarvis will extract tasks, signals, and opportunities — and sync everything to your CRM automatically.
              </div>
              <div className="mtg-placeholder-items">
                <div className="mtg-placeholder-item">✅ Action items → Tasks DB</div>
                <div className="mtg-placeholder-item">⚡ Intel triggers → Signals DB</div>
                <div className="mtg-placeholder-item">🎯 Buying signals → Leads DB</div>
                <div className="mtg-placeholder-item">🏢 Account record updated</div>
                <div className="mtg-placeholder-item">✉️ Cyrano drafts created</div>
              </div>
            </div>
          )}

          {/* Processing */}
          {phase === 'processing' && (
            <div className="mtg-results-placeholder">
              <div className="mtg-analyzing-wrap">
                <div className="mtg-analyzing-ring" />
                <div className="mtg-placeholder-icon" style={{ position: 'absolute' }}>🧠</div>
              </div>
              <div className="mtg-placeholder-title" style={{ marginTop: 20 }}>
                {processingStep || 'Analyzing…'}
              </div>
              <div className="mtg-placeholder-sub">
                Running the full 3-phase pipeline: Jarvis → Victor → Cyrano
              </div>
              <div className="mtg-pipeline-steps">
                <div className="mtg-pipeline-step mtg-step-active">
                  <span className="mtg-step-dot" /> Phase 1: Jarvis extracting intelligence
                </div>
                <div className="mtg-pipeline-step">
                  <span className="mtg-step-dot mtg-step-dot-pending" /> Phase 2: Victor strategic review
                </div>
                <div className="mtg-pipeline-step">
                  <span className="mtg-step-dot mtg-step-dot-pending" /> Phase 3: Cyrano drafting outreach
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {phase === 'done' && results && (
            <div className="mtg-results dm-fade-in" ref={resultsRef}>

              {/* Pipeline summary bar */}
              <div className="mtg-pipeline-bar">
                <div className={`mtg-pipeline-chip ${results.pipeline?.phase1_jarvis === 'complete' ? 'chip-ok' : 'chip-err'}`}>
                  Jarvis ✓
                </div>
                <div className="mtg-pipeline-arrow">→</div>
                <div className={`mtg-pipeline-chip ${results.pipeline?.phase2_victor === 'complete' ? 'chip-ok' : 'chip-warn'}`}>
                  Victor {results.pipeline?.phase2_victor === 'complete' ? '✓' : '⚠'}
                </div>
                <div className="mtg-pipeline-arrow">→</div>
                <div className={`mtg-pipeline-chip ${results.pipeline?.phase3_cyrano?.thankyou === 'drafted' ? 'chip-ok' : 'chip-warn'}`}>
                  Cyrano {results.pipeline?.phase3_cyrano?.thankyou === 'drafted' ? '✓' : '⚠'}
                </div>
              </div>

              {/* Summary */}
              <div className="mtg-result-section">
                <div className="mtg-result-section-title">📋 Meeting Summary</div>
                <p className="mtg-summary-text">{results.analysis?.summary}</p>
              </div>

              {/* Key insights */}
              {results.analysis?.keyInsights?.length > 0 && (
                <div className="mtg-result-section">
                  <div className="mtg-result-section-title">💡 Key Insights</div>
                  <ul className="mtg-insight-list">
                    {results.analysis.keyInsights.map((insight, i) => (
                      <li key={i} className="mtg-insight-item">{insight}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tasks */}
              {results.results?.tasks?.length > 0 && (
                <div className="mtg-result-section">
                  <div className="mtg-result-section-title">
                    ✅ Tasks Created
                    <span className="mtg-result-count">{results.results.tasks.length}</span>
                  </div>
                  <div className="mtg-result-items">
                    {results.results.tasks.map((t, i) => (
                      <div key={i} className="mtg-result-item">
                        <div className="mtg-result-item-name">{t.name}</div>
                        <span className={`mtg-priority-pill mtg-priority-${(t.priority || 'medium').toLowerCase()}`}>
                          {t.priority || 'Medium'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Signals */}
              {results.results?.signals?.length > 0 && (
                <div className="mtg-result-section">
                  <div className="mtg-result-section-title">
                    ⚡ Signals Captured
                    <span className="mtg-result-count">{results.results.signals.length}</span>
                  </div>
                  <div className="mtg-result-items">
                    {results.results.signals.map((s, i) => (
                      <div key={i} className="mtg-result-item">
                        <div className="mtg-result-item-name">{s.title}</div>
                        <span className={`mtg-strength-pill mtg-strength-${(s.strength || 'moderate').toLowerCase()}`}>
                          {s.strength || 'Moderate'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Opportunity */}
              {results.analysis?.opportunityDetected && (
                <div className="mtg-result-section mtg-opportunity-section">
                  <div className="mtg-result-section-title">🎯 Opportunity Detected</div>
                  <p className="mtg-opportunity-text">{results.analysis.opportunityHypothesis}</p>
                  {results.analysis.opportunityDealSize && (
                    <div className="mtg-deal-size">
                      Est. deal size: <strong>{results.analysis.opportunityDealSize}</strong>
                    </div>
                  )}
                </div>
              )}

              {/* Contacts — editable */}
              <div className="mtg-result-section">
                <div className="mtg-result-section-title">
                  👤 Contacts Mentioned
                  <span className="mtg-result-count">{editableContacts.length}</span>
                  <button
                    className="mtg-edit-add-btn"
                    title="Add contact"
                    onClick={() => {
                      setEditableContacts(prev => [...prev, { name: '', title: '', company: '', isInternal: false, notes: '' }]);
                      setContactsDirty(true);
                    }}
                  >
                    + Add
                  </button>
                </div>

                {editableContacts.length === 0 && (
                  <div className="mtg-no-contacts">No contacts detected. Add them manually.</div>
                )}

                <div className="mtg-contact-edit-list">
                  {editableContacts.map((c, i) => (
                    <div key={i} className="mtg-contact-edit-row">
                      <div className="mtg-contact-edit-fields">
                        <input
                          className="mtg-contact-input mtg-contact-name"
                          placeholder="Full name"
                          value={c.name}
                          onChange={e => {
                            const updated = [...editableContacts];
                            updated[i] = { ...updated[i], name: e.target.value };
                            setEditableContacts(updated);
                            setContactsDirty(true);
                          }}
                        />
                        <input
                          className="mtg-contact-input mtg-contact-title"
                          placeholder="Title"
                          value={c.title || ''}
                          onChange={e => {
                            const updated = [...editableContacts];
                            updated[i] = { ...updated[i], title: e.target.value };
                            setEditableContacts(updated);
                            setContactsDirty(true);
                          }}
                        />
                        <input
                          className="mtg-contact-input mtg-contact-company"
                          placeholder="Company"
                          value={c.company || ''}
                          onChange={e => {
                            const updated = [...editableContacts];
                            updated[i] = { ...updated[i], company: e.target.value };
                            setEditableContacts(updated);
                            setContactsDirty(true);
                          }}
                        />
                        <label className="mtg-contact-internal-label">
                          <input
                            type="checkbox"
                            checked={!!c.isInternal}
                            onChange={e => {
                              const updated = [...editableContacts];
                              updated[i] = { ...updated[i], isInternal: e.target.checked };
                              setEditableContacts(updated);
                              setContactsDirty(true);
                            }}
                          />
                          Internal
                        </label>
                      </div>
                      <button
                        className="mtg-contact-delete-btn"
                        title="Remove contact"
                        onClick={() => {
                          setEditableContacts(prev => prev.filter((_, idx) => idx !== i));
                          setContactsDirty(true);
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                {/* Re-run Victor button — only shown when contacts have been edited */}
                {contactsDirty && (
                  <div className="mtg-rerun-wrap">
                    <div className="mtg-rerun-hint">
                      ✏️ Contacts updated — re-run Victor so outreach drafts use the correct names.
                    </div>
                    <button
                      className="mtg-rerun-btn"
                      onClick={rerunVictor}
                      disabled={rerunStatus === 'running'}
                    >
                      {rerunStatus === 'running' ? (
                        <><span className="mtg-btn-spinner" /> Updating Victor &amp; Cyrano…</>
                      ) : (
                        '🔄 Update &amp; Re-run Victor'
                      )}
                    </button>
                    {rerunStatus === 'done' && (
                      <div className="mtg-rerun-success">
                        ✓ Victor re-queued with corrected contacts. Check Drafts in ~30s.
                      </div>
                    )}
                    {rerunStatus === 'error' && (
                      <div className="mtg-rerun-error">
                        ⚠ Re-run failed — try again, or edit the contacts on the account.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Cyrano drafts notice */}
              {(results.results?.cyranoThankyou || results.results?.cyranoInternal) && (
                <div className="mtg-cyrano-notice">
                  <span className="mtg-cyrano-icon">✉️</span>
                  <span>
                    {[
                      results.results.cyranoThankyou && 'Thank you email',
                      results.results.cyranoInternal && 'Internal summary',
                    ].filter(Boolean).join(' + ')} drafted by Cyrano — pending your approval.
                  </span>
                  <button
                    className="mtg-cyrano-link"
                    onClick={() => onNavigate('comms-drafts')}
                  >
                    Review Drafts →
                  </button>
                </div>
              )}

              {/* Victor partial warning */}
              {results.pipeline?.phase2_victor !== 'complete' && (
                <div className="mtg-warning-notice">
                  ⚠️ Victor's strategic review is still processing. Check back in a moment — it will appear on the account's Meetings tab when complete.
                </div>
              )}

              {/* Errors (non-fatal) */}
              {results.results?.errors?.length > 0 && (
                <div className="mtg-result-section">
                  <div className="mtg-result-section-title" style={{ color: '#f59e0b' }}>
                    ⚠️ Partial Sync Issues ({results.results.errors.length})
                  </div>
                  <div className="mtg-error-list">
                    {results.results.errors.map((e, i) => (
                      <div key={i} className="mtg-error-item">{e.type}: {e.error}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mtg-done-actions">
                {(selectedAccount || results.accountId) && (
                  <button
                    className="mtg-view-account-btn"
                    onClick={() => onNavigate('account-detail', results.accountId || selectedAccount?.id)}
                  >
                    View Account →
                  </button>
                )}
                <button className="mtg-new-meeting-btn" onClick={restart}>
                  + New Meeting
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
